import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import DatePicker from "../../../component-lib/Atoms/DatePicker/DatePicker";
import TooltipForTable from "../../../component-lib/Molecules/tooltip/TooltipForTable";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ReportsService from "../../../services/ReportsService";
import { componentKey } from "../ReportsSlice";
import { getDailyOrdersReport } from "../ReportsSaga";
import {
  CSV_HEADERS_DAILY_ORDERS,
  ROWS_PER_PAGE_OPTIONS,
  formatReportDate,
  toApiStartDate,
  toApiEndDate,
} from "../ReportConstants";
import { destructStatusData } from "../../../utils/statusConstants";
import DownloadCsvButton from "../components/DownloadCsvButton";
import StatusChip from "../components/StatusChip";

type DailyOrdersReportTabProps = { facilityId: string };

const joinItems = (items: unknown) =>
  Array.isArray(items) ? items.filter(Boolean).join(", ") : "";

const getOrderItems = (row: Record<string, any>): Array<Record<string, any>> =>
  Array.isArray(row?.orderItems) ? row.orderItems : [];

// Per-item quantity label, e.g. "Qty: 2 Tablets" — blank when no quantity.
const formatQty = (item: Record<string, any>) => {
  const qty = item?.quantity;
  if (qty === null || qty === undefined || qty === "") return "";
  return `Qty: ${qty}${item?.unit ? ` ${item.unit}` : ""}`;
};

const chipBaseStyle: React.CSSProperties = {
  borderRadius: "46px",
  padding: "2px 10px",
  fontSize: "12px",
  whiteSpace: "nowrap",
  lineHeight: "18px",
};
const qtyChipStyle: React.CSSProperties = {
  ...chipBaseStyle,
  background: "#EAF2FB",
  color: "#005596",
};
const freqChipStyle: React.CSSProperties = {
  ...chipBaseStyle,
  background: "#EEF2F6",
  color: "#475569",
  textTransform: "capitalize",
};

const DailyOrdersReportTab = ({ facilityId }: DailyOrdersReportTabProps) => {
  const dispatch = useDispatch();
  const { dailyOrdersReport } = useSelector(
    (state: Record<string, any>) => state[componentKey] as Record<string, any>
  );

  // Report only shows orders; the medications view was removed per requirement.
  const category = "orders";
  const [pageNumber, setPageNumber] = useState(1);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);

  const rows: Array<Record<string, any>> = dailyOrdersReport?.data || [];

  useEffect(() => {
    dispatch(
      getDailyOrdersReport({
        pageNumber,
        limit: rowPerPage,
        category,
        facilityId: facilityId || undefined,
        startDate: toApiStartDate(startDate),
        endDate: toApiEndDate(endDate),
      })
    );
  }, [facilityId, category, pageNumber, rowPerPage, startDate, endDate]);

  const isAnyFilterApplied = Boolean(startDate || endDate);

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setPageNumber(1);
  };

  const columns = [
    {
      field: "date",
      label: <div>Date</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {formatReportDate(row?.date)}
        </p>
      ),
    },
    {
      field: "patientName",
      label: <div>Patient Name</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.patientName || "-"}</p>
      ),
    },
    {
      field: "bsaId",
      label: <div>BSA ID</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.bsaId || "-"}</p>
      ),
    },
    {
      field: "facilityName",
      label: <div>Facility</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.facilityName || "-"}</p>
      ),
    },
    {
      field: "orderType",
      label: <div>Order Type</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.orderType || "-"}</p>
      ),
    },
    {
      field: "items",
      label: <div>Items</div>,
      renderLogic: (row: Record<string, any>) => (
        <TooltipForTable label={joinItems(row?.items) || "-"} />
      ),
    },
    {
      field: "orderDetails",
      label: <div>Order Details</div>,
      renderLogic: (row: Record<string, any>) => {
        const orderItems = getOrderItems(row);
        if (!orderItems.length)
          return <span className="text-neutral-90 font-normal">-</span>;
        return (
          <div className="flex flex-col gap-2" style={{ minWidth: "240px" }}>
            {orderItems.map((item, idx) => {
              const qty = formatQty(item);
              return (
                <div key={idx} className="flex items-center gap-2 flex-wrap">
                  <span className="text-neutral-90 font-weight-500">
                    {item?.name || "-"}
                  </span>
                  {qty && <span style={qtyChipStyle}>{qty}</span>}
                  {item?.frequency && (
                    <span style={freqChipStyle}>{item.frequency}</span>
                  )}
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: Record<string, any>) => (
        <StatusChip status={row?.status} />
      ),
    },
  ];

  const fetchCsvRows = async () => {
    const response = await ReportsService.getDailyOrdersReport({
      limit: "All",
      category,
      facilityId: facilityId || undefined,
      startDate: toApiStartDate(startDate),
      endDate: toApiEndDate(endDate),
    });
    const data: Array<Record<string, any>> = response?.data?.data || [];
    return data.map((row) => {
      const orderItems = getOrderItems(row);
      return {
        date: formatReportDate(row?.date),
        patientName: row?.patientName,
        bsaId: row?.bsaId,
        facilityName: row?.facilityName,
        orderType: row?.orderType,
        items: joinItems(row?.items),
        quantity: orderItems.length
          ? orderItems
              .map((item) =>
                item?.quantity === null ||
                item?.quantity === undefined ||
                item?.quantity === ""
                  ? "-"
                  : `${item.quantity}${item?.unit ? ` ${item.unit}` : ""}`
              )
              .join(", ")
          : "-",
        frequency: orderItems.length
          ? orderItems.map((item) => item?.frequency || "-").join(", ")
          : row?.frequency,
        status: destructStatusData(row?.status),
      };
    });
  };

  return (
    <div className="mt-5">
      <div className="flex flex-wrap mb-4 gap-3 justify-end items-center">
        <div style={{ minWidth: "180px" }}>
          <DatePicker
            label="From Date"
            value={startDate}
            maxDate={endDate || undefined}
            onChangeCb={(value) => {
              setPageNumber(1);
              setStartDate(value);
            }}
          />
        </div>
        <div style={{ minWidth: "180px" }}>
          <DatePicker
            label="To Date"
            value={endDate}
            minDate={startDate || undefined}
            onChangeCb={(value) => {
              setPageNumber(1);
              setEndDate(value);
            }}
          />
        </div>
        <ClearFiltersButton
          onClear={handleClearFilters}
          disabled={!isAnyFilterApplied}
        />
        <DownloadCsvButton
          headers={CSV_HEADERS_DAILY_ORDERS}
          filename="daily-orders-report.csv"
          fetchRows={fetchCsvRows}
          disabled={!rows.length}
        />
      </div>

      <Table
        columns={columns}
        rows={rows}
        noDataFoundMessage="No orders found"
        paginationProps={{
          isPagination: true,
          page: pageNumber,
          totalPages: dailyOrdersReport?.totalPages,
          totalCount: dailyOrdersReport?.totalRecords,
          limit: rowPerPage,
          rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
          onPageChange: (page: number) => setPageNumber(page),
          handleChangeRowsPerPage: (limitValue: string) => {
            setRowPerPage(limitValue);
            setPageNumber(1);
          },
        }}
      />
    </div>
  );
};

export default DailyOrdersReportTab;
